import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { CircularProgress, Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import LogoSL from "assets/images/logo-sl.png";
// import LogoUPRO from "assets/images/logo-upro.png";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import EditableText from "components/EditableText";
import PdfHeader from "../components/pdfHeader";
import { formatMoneyPunto } from "utils/formatters";
import { nombreCompraDetalle, unidadCompraDetalle } from "utils/presentacionCompra";

const textosIniciales = [
  " ",
  " ",
  " ",
  "--/--/--",
];

const tipoResponsables = [
  { id: 1, label: "IVA Responsable Inscripto" },
  { id: 2, label: "IVA Responsable no Inscripto" },
  { id: 3, label: "IVA no Responsable" },
  { id: 4, label: "IVA Sujeto Exento" },
  { id: 5, label: "Consumidor Final" },
  { id: 6, label: "Responsable Monotributo" },
  { id: 7, label: "Sujeto no Categorizado" },
  { id: 8, label: "Proveedor del Exterior" },
  { id: 9, label: "Cliente del Exterior" },
  { id: 10, label: "IVA Liberado – Ley Nº 19.640" },
  { id: 11, label: "IVA Responsable Inscripto – Agente de Percepción" },
  { id: 12, label: "Pequeño Contribuyente Eventual" },
  { id: 13, label: "Monotributista Social" },
  { id: 14, label: "Pequeño Contribuyente Eventual Social" },
];

const tipoDocumentos = [
  { id: 80, label: "CUIT" },
  { id: 86, label: "CUIL" },
  { id: 96, label: "DNI" },
]

const PreAdjudicacionProveedor = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { lg } = boxShadows;

  const [textArray, setTextArray] = useState(textosIniciales);
  const [editable, setEditable] = useState(true);

  const [data, setData] = useState(null);
  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query getPedido($id: ID) {
        pedidosCompra(filter: { id: $id }) {
          id
          numeroExpediente
          cotizaciones{
            id
            proveedor{
              id
              razonSocial
              nombre
              apellido
              documento
              tipoDocumento
              tipoResponsable
              localidad{
                nombre
                provincia{
                  nombre
                }
              }
            }
            detalles{
              id
            }
          }
          detalles{
            id
            numero
            nombreCompra
            unidadNombre
            unidadAbreviatura
            requierePack
            unidadPack
            materiaPrima{
              id
              nombre
              nombreCompra
              tipo
              tipoUnidad {
                id
                nombre
              }
            }
            nombreServicio
            cantidadVerificada
            cotizaciones{
              id
              precio
              materiaPrimaAlternativo
              nombreAlternativo
              observacion
            }
            cotizacionSeleccionada{
              id
              precio
              materiaPrimaAlternativo
              nombreAlternativo
              observacion
            }
            cotizacionSeleccionadaDesc
          }
        }
      }
    `,
  );

  const fetchPedido = async () => {
    const res = await getPedido({ variables: { id } });
    let pedido = res.data.pedidosCompra[0];
    if (!pedido) return;

    let selecciones = [];

    pedido.detalles
      .filter(d => d.cotizaciones?.length > 0)
      .forEach((detalle, index) => {
        let seleccion = {
          idProveedor: null,
          cotizacion: null,
          desc: null,
          detalle: detalle
        };

        if (detalle.cotizacionSeleccionada) {
          seleccion.cotizacion = detalle.cotizacionSeleccionada;
          seleccion.desc = detalle.cotizacionSeleccionadaDesc;

          seleccion.idProveedor = pedido.cotizaciones.find(
            c => c.detalles.find(
              d => d.id == seleccion.cotizacion.id
            )
          )?.proveedor.id;

          selecciones.push(seleccion);
        }
      });

    let idsProvedores = [...new Set(selecciones.map(s => s.idProveedor))];
    let dt = idsProvedores.map(idProveedor => {
      let proveedor = pedido.cotizaciones.find(c => c.proveedor.id == idProveedor).proveedor;
      let items = selecciones.filter(s => s.idProveedor == idProveedor);
      return {
        ...proveedor,
        items: items,
        total: items.reduce((acc, item) => acc + (item.cotizacion.precio * item.detalle.cantidadVerificada), 0)
      }
    });

    setData({
      id: pedido.id,
      numeroExpediente: pedido.numeroExpediente,
      proveedores: dt,
      total: dt.reduce((acc, p) => acc + p.total, 0)
    });
  };


  useEffect(() => {
    if (id) {
      fetchPedido();
    }
  }, [id]);

  useEffect(() => {
    if (data) {
      const newTextArray = [...textArray];
      newTextArray[0] = data.id;
      newTextArray[1] = data.id;

      setTextArray(newTextArray);
    }
  }, [data]);

  const handleGeneratePdf = async () => {
    setEditable(false);
    const document = documentsRef.current;
    const doc = new jsPDF({
      format: "a4",
      unit: "px",
      orientation: "landscape",
      hotfixes: ["px_scaling"],
      compress: true,
    });

    // Define el footer con el número de página
    const addFooter = (doc, currentPage, totalPages) => {
      doc.setFontSize(8);
      doc.text(`Página ${currentPage} de ${totalPages}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, {
        align: "center",
      });
    };

    // Genera el contenido principal en el PDF
    await doc.html(document, {
      margin: [40, 0, 40, 0],
      autoPaging: "text",
      callback: async function (doc) {
        const totalPages = doc.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          addFooter(doc, i, totalPages);
        }

        // Guarda el archivo PDF
        doc.save(`exd-pedidoNro-${data.id}-preAdjudicacionPorProveedor.pdf`);
      },
    });

    setEditable(true);
  };

  const handleTextChange = (e, index) => {
    const newTextArray = [...textArray];
    newTextArray[index] = e.target.innerText;
    setTextArray(newTextArray);
  };

  if (!data) return (
    <EmpleadoLayout>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
        {loading ? (<>
          <CircularProgress size={40} color="primary" />
          <SoftTypography variant="h5">Cargando</SoftTypography>
        </>) : (<>
          <Icon fontSize="large">search_off</Icon>
          <SoftTypography variant="h5">No se encontró el pedido</SoftTypography>
        </>)}
      </SoftBox>
    </EmpleadoLayout>
  );

  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
      >
        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "1123px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            {/* ACA VA TODO EL CODIGO */}
            <PdfHeader />

            <SoftBox display="flex" flexDirection="column" gap={2} lineHeight={1.5} fontSize="12px">
              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} fontWeight="bold" px={35} py={1} textAlign="center">
                  INFORME DE PRE-ADJUDICACION POR PROVEEDOR - CONCURSO DE PRECIOS OTROS ORGANISMOS
                  PROCESO N°:{" "}
                  <EditableText
                    tooltip="Número"
                    editable={editable}
                    content={textArray[0]}
                    handleOnBlur={(e) => handleTextChange(e, 0)}
                  />
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Método de Adjudicación </Grid>
                  <Grid item xs={6} px={1}>POR ITEM INDIVIDUAL AL MENOR PRECIO OFERTADO</Grid>

                  <Grid item xs={2} px={1}>Total Informe</Grid>
                  <Grid item xs={2} px={1} textAlign="right">$ {formatMoneyPunto(data.total || 0)}</Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}
                >
                  <Grid item xs={2} px={1}>Solicitud N° </Grid>
                  <Grid item xs={2} px={1}>
                    <EditableText
                      tooltip="Número"
                      editable={editable}
                      content={textArray[1]}
                      handleOnBlur={(e) => handleTextChange(e, 1)}
                    />
                  </Grid>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  sx={{
                    '& > *:not(:last-child)': { borderRight: "1pt solid black" },
                    '& > *:nth-child(odd)': { backgroundColor: "#f0f0f0", fontWeight: "bold" },
                  }}>
                  <Grid item xs={2} px={1}>Unidad Solicitante </Grid>
                  <Grid item xs={10} px={1}>{"FUNDACIÓN UPrO"}</Grid>
                </Grid>
              </Grid>

              <SoftTypography variant="h6">Cotizaciones Evaluadas por Proveedor</SoftTypography>

              <SoftBox>
                {data.proveedores.map((proveedor, index) => (
                  <SoftBox key={index} border="1pt solid black" mb={2}>
                    {/* Encabezado de la tabla */}
                    <Grid
                      container
                      backgroundColor="#f0f0f0"
                      textAlign="center"
                      fontWeight="bold"
                      sx={{
                        '& > *': {
                          borderRight: "1pt solid black",
                          borderBottom: "1pt solid black"
                        },
                        '& > *:last-child': {
                          borderRight: "none"
                        }
                      }}
                    >
                      <Grid item xs={3} px={0.5} py={1}>Proveedor</Grid>
                      <Grid item xs={0.8} px={0.5} py={1}>Item</Grid>
                      <Grid item xs={4} px={0.5} py={1}>Descripción / Observación / Motivo</Grid>
                      <Grid item xs={0.8} px={0.5} py={1}>Cantidad</Grid>
                      <Grid item xs={0.8} px={0.5} py={1}>Unidad</Grid>
                      <Grid item xs={1.3} px={0.5} py={1}>Monto Unitario</Grid>
                      <Grid item xs={1.3} px={0.5} py={1}>Total</Grid>
                    </Grid>

                    {/* Contenedor de filas con proveedor agrupado */}
                    <SoftBox sx={{ display: "flex" }}>
                      {/* Columna del proveedor (fija a la izquierda) */}
                      <SoftBox
                        sx={{
                          width: "25%", // xs={3} equivale al 25%
                          borderRight: "1pt solid black",
                          backgroundColor: "#f9f9f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "8px",
                          fontWeight: "bold"
                        }}
                      >
                        <SoftBox textAlign="center">
                          {[proveedor.razonSocial, proveedor.nombre, proveedor.apellido].join(" ").toUpperCase()}
                          <br />
                          {`${tipoDocumentos.find(t => t.id == proveedor.tipoDocumento)?.label || "DOCUMENTO"} ${proveedor.documento}`}
                        </SoftBox>
                      </SoftBox>

                      {/* Columnas de datos de los items */}
                      <SoftBox sx={{ width: "75%" }}>
                        {proveedor.items.map((item, itemIndex) => (
                          <Grid
                            key={itemIndex}
                            container
                            sx={{
                              '& > *': {
                                borderRight: "1pt solid black",
                                borderBottom: itemIndex === proveedor.items.length - 1 ? "none" : "1pt solid black"
                              },
                              '& > *:last-child': {
                                borderRight: "none"
                              },
                              backgroundColor: itemIndex % 2 === 0 ? "white" : "#f0f0f0"
                            }}
                          >
                            {/* Datos del item (ajustados para ocupar el 75% restante) */}
                            <Grid item xs={1.07} px={0.5} py={1} textAlign="center">{item.detalle.numero}</Grid>
                            <Grid item xs={5.33} px={0.5} py={1}>
                              {item?.detalle?.requierePack === true && item?.detalle?.unidadPack ? nombreCompraDetalle(item.detalle) : [
                                item.cotizacion.nombreAlternativo?.toUpperCase(),
                                item.cotizacion.materiaPrimaAlternativo ? null : nombreCompraDetalle(item.detalle),
                                item.cotizacion.materiaPrimaAlternativo ? "(Alternativo)" : null,
                                item.detalle.observacion,
                              ].filter(e => e).join(" ")}
                              {item.desc && (
                                <strong>{" " + item.desc}</strong>
                              )}
                            </Grid>
                            <Grid item xs={1.07} px={0.5} py={1} textAlign="center">
                              {item.detalle.cantidadVerificada}
                            </Grid>
                            <Grid item xs={1.07} px={0.5} py={1} textAlign="center">
                              {unidadCompraDetalle(item.detalle)}
                            </Grid>
                            <Grid item xs={1.73} px={0.5} py={1} textAlign="right">
                              $ {formatMoneyPunto(item.cotizacion.precio || 0)}
                            </Grid>
                            <Grid item xs={1.73} px={0.5} py={1} textAlign="right">
                              $ {formatMoneyPunto(item.cotizacion.precio * item.detalle.cantidadVerificada || 0)}
                            </Grid>
                          </Grid>
                        ))}
                      </SoftBox>
                    </SoftBox>

                    {/* Fila de total del proveedor */}
                    <Grid container sx={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}>
                      <Grid item xs={12} px={1} py={1} textAlign="right">
                        TOTAL {[proveedor.razonSocial, proveedor.nombre, proveedor.apellido].join(" ").toUpperCase()}: $ {formatMoneyPunto(proveedor.total)}
                      </Grid>
                    </Grid>
                  </SoftBox>
                ))}
              </SoftBox>

              <SoftBox>
                <SoftBox fontWeight="bold" borderBottom="1pt solid black">Padron de Proveedores</SoftBox>
                {data?.proveedores?.map((p, index) => (
                  <SoftBox key={index}>
                    - <strong>{[p.razonSocial, p.nombre, p.apellido].join(" ").toUpperCase()}</strong>
                    {" "}
                    {[
                      tipoDocumentos.find(t => t.id == p.tipoDocumento)?.label || "Documento",
                      p.documento,
                      p.localidad?.nombre,
                      p.localidad?.provincia?.nombre,
                      tipoResponsables.find(t => t.id == p.tipoResponsable)?.label
                    ].filter(e => e).join(" ").toUpperCase()}
                  </SoftBox>
                ))}
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </div>
        <SoftBox
          hidden={!editable}
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end" id="button">
            <Grid item>
              <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default PreAdjudicacionProveedor;
